import React, { useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import PreviewCanvas from '../../../organisms/preview-canvas/PreviewCanvas';
import { getValue } from '../../utils/tools';
import { DEFAULT_ADVANCE } from './WalkthroughAdvanceControl';
import './WalkthroughPlayerV2.scss';

// Walkthrough Editor v2 — new learner renderer (published + editor Preview).
//
// Presentational: StepsViewer keeps ownership of steps/index and all hub
// progress-saving; this component only renders the redesigned learner layout
// (mockup 07) and calls back the handlers it's given. The real interactive
// screenshot + hotspots are reused as-is via PreviewCanvas — only re-housed in
// a browser frame. Theming reads the walkthrough appearance config, including
// the split accent colors (wp section): step progress, button, and highlight.

const richHtml = (value) => DOMPurify.sanitize(String(value || '').replace(/\n/g, '<br>'));

const WalkthroughPlayerV2 = ({ title, steps, index, configColors, onNext, onPrev, onGoto, onChange, embed, id, showNav = true, brandName, companySlug, learner, fullPage = false }) => {
  const list = Array.isArray(steps) ? steps : [];
  const current = list[index] || {};
  const isFirst = index <= 0;
  const isLast = index >= list.length - 1;

  const primary = configColors && configColors.primary;
  const stepColor = getValue(configColors, 'wp', 'wpStepColor') || primary;
  const buttonColor = getValue(configColors, 'wp', 'wpButtonColor') || primary;
  const pageBg = getValue(configColors, 'mca', 'mcaBgColor') || (configColors && configColors.background);
  const titleColor = getValue(configColors, 'tn', 'tnTextColor') || '#16232E';
  const navBg = getValue(configColors, 'tn', 'tnBGColor') || '#FFFFFF';
  const navLink = getValue(configColors, 'tn', 'tnLinkColor') || '#0E7C86';
  const logo = getValue(configColors, 'tn', 'tnLogo');
  // Right side of the nav: the learner's name once their session is identified,
  // otherwise a magic-link sign-in link (same flow the Hub header uses).
  const learnerName = learner
    ? (`${learner.first_name || ''} ${learner.last_name || ''}`.trim() || learner.email)
    : '';
  const signInHref = companySlug ? `/${companySlug}/passwordless_sign_in` : '#';

  const headlineStyle = {
    fontFamily: getValue(configColors, 'mca', 'mcaStepHeadlineFont'),
    fontSize: getValue(configColors, 'mca', 'mcaStepHeadlineFontSize'),
    color: getValue(configColors, 'mca', 'mcaStepHeadlineColor')
  };
  const descStyle = {
    fontFamily: getValue(configColors, 'mca', 'mcaStepDescriptionFont'),
    fontSize: getValue(configColors, 'mca', 'mcaStepDescriptionFontSize'),
    color: getValue(configColors, 'mca', 'mcaStepDescriptionColor')
  };

  const stepHeadline = current.title || current.name || '';

  // BL-153 — honor the step's advance triggers at runtime:
  //   • hotspot   → clicking the hotspot advances (default)
  //   • nextButton→ show a Next button
  //   • auto      → advance on its own after autoDelay seconds
  const adv = { ...DEFAULT_ADVANCE, ...(current.advance || {}) };
  const hotspotAdvances = !!adv.hotspot && !adv.auto;
  // Always allow Finish on the last step; otherwise show Next when the admin
  // enabled it, or when nothing else would let the learner move on (safety).
  const showNext = isLast || (!adv.auto && (adv.nextButton || !adv.hotspot));

  // Completion (BL — walkthrough completion screen). Finishing the last step —
  // via Finish, the last hotspot, or an auto-advance — shows a completion
  // screen with Restart (replay in place) and Exit (back to the learner Hub).
  const [done, setDone] = useState(false);
  const handleForward = () => {
    if (onNext) onNext();       // records hub completion / advances a step
    if (isLast) setDone(true);  // last step → show the completion screen
  };
  const restart = () => { setDone(false); if (onGoto) onGoto(list[0], 0); };
  const exitToHub = () => { window.location.href = companySlug ? `/${companySlug}` : '/'; };

  // The screenshot canvas (PreviewCanvas) renders at a fixed 1024px stage.
  // Scale it down to fit the card so the layout stays compact and the footer
  // (instruction + Back/Next) is visible, matching the mockup.
  const cardRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  useEffect(() => {
    const compute = () => {
      const el = cardRef.current;
      if (!el || !el.clientWidth) return;
      // Fill the available width — scale the 1024px stage up or down to the card.
      setZoom(Math.min(1.75, el.clientWidth / 1024));
    };
    compute();
    // The screenshot canvas sizes itself after the image loads, so recompute a
    // few times to catch the final layout, plus on resize.
    const timers = [120, 400, 900, 1600].map((ms) => setTimeout(compute, ms));
    window.addEventListener('resize', compute);
    return () => { timers.forEach(clearTimeout); window.removeEventListener('resize', compute); };
  }, [index]);

  // Auto-advance after the configured delay when the step uses the Auto trigger.
  useEffect(() => {
    if (!adv.auto || done) return undefined;
    const t = setTimeout(() => { handleForward(); }, Math.max(1, adv.autoDelay || 3) * 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, adv.auto, adv.autoDelay]);

  return (
    <div className={`un-wpv${fullPage ? ' un-wpv--full' : ''}`} style={{ background: pageBg }}>
      {showNav && (
        <div className="un-wpv__nav" style={{ background: navBg }}>
          <div className="un-wpv__nav-left">
            {logo
              ? <img className="un-wpv__nav-logo" src={logo} alt={brandName || 'Logo'} />
              : <span className="un-wpv__nav-brand" style={{ color: titleColor }}>{brandName}</span>}
            {fullPage && title
              ? <span className="un-wpv__nav-title" style={{ color: titleColor }}>{title}</span>
              : null}
          </div>
          <div className="un-wpv__nav-right">
            {learnerName
              ? <span className="un-wpv__nav-user" style={{ color: titleColor }}>{learnerName}</span>
              : <a className="un-wpv__nav-signin" style={{ color: navLink }} href={signInHref}>Magic Link Learner Sign In</a>}
          </div>
        </div>
      )}

      <div className="un-wpv__steps" role="tablist" aria-label="Steps">
        {list.map((s, i) => {
          const done = i <= index;
          return (
            <React.Fragment key={s.id || i}>
              {i > 0 && (
                <span className="un-wpv__line" style={done ? { background: stepColor } : null} />
              )}
              <button
                type="button"
                className={`un-wpv__dot${done ? ' on' : ''}`}
                style={done ? { background: stepColor, color: '#fff', boxShadow: `0 0 0 4px ${stepColor}22` } : null}
                onClick={() => onGoto && onGoto(current, i)}
                aria-label={`Step ${i + 1}`}
                aria-selected={i === index}
              >
                {i + 1}
              </button>
            </React.Fragment>
          );
        })}
      </div>

      <div className="un-wpv__card" ref={cardRef}>
        <div className="un-wpv__bar">
          <span className="un-wpv__dots"><i /><i /><i /></span>
          <span className="un-wpv__url">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="10" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
            {current.url || 'app.example.com'}
          </span>
        </div>
        <div className="un-wpv__view" style={{ zoom }}>
          <PreviewCanvas configColors={configColors} embed={embed} id={id} step={current} onChange={done || !hotspotAdvances ? () => {} : handleForward} />
        </div>

        {done && (
          <div className="un-wpv__done-overlay">
            <div className="un-wpv__done-modal">
              <div className="un-wpv__done-badge" style={{ background: stepColor }}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
              </div>
              <div className="un-wpv__done-title" style={{ color: titleColor }}>You're all done</div>
              <div className="un-wpv__done-sub">You've completed {title || 'this walkthrough'}.</div>
              <div className="un-wpv__done-meta">{list.length} of {list.length} steps completed</div>
              <div className="un-wpv__done-btns">
                <button type="button" className="un-wpv__back" onClick={restart}>&#8635; Restart</button>
                <button type="button" className="un-wpv__next" style={{ background: buttonColor }} onClick={exitToHub}>Exit</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {!done && (
        <div className="un-wpv__foot">
          <div className="un-wpv__instr">
            {stepHeadline ? <div className="un-wpv__hl" style={headlineStyle}>{stepHeadline}</div> : null}
            {current.description ? (
              <div className="un-wpv__desc" style={descStyle} dangerouslySetInnerHTML={{ __html: richHtml(current.description) }} />
            ) : null}
          </div>
          <div className="un-wpv__btns">
            <button type="button" className="un-wpv__back" onClick={onPrev} disabled={isFirst}>&larr; Back</button>
            {showNext && (
              <button type="button" className="un-wpv__next" style={{ background: buttonColor }} onClick={handleForward}>
                {isLast ? 'Finish' : 'Next'} &rarr;
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WalkthroughPlayerV2;
